import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tag, ChevronDown, ChevronUp, Copy, Check, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

/**
 * UTM Bulk Tagger — client-side ops tool.
 *
 * Paste a list of outbound URLs (one per line), enter utm_source /
 * utm_campaign / etc., get a copy-pasteable list back. Zero backend
 * dependency — this is a productivity shortcut for the person scheduling
 * a dozen LinkedIn posts, not a data source.
 *
 * Design decisions worth calling out:
 *  - Existing UTM params on an input line are OVERWRITTEN, non-UTM params
 *    are preserved. Ops usually want to re-tag, not merge.
 *  - Empty utm_source is a soft warning (yellow), not a block — some ops
 *    workflows only want to add a utm_campaign to an already-tagged URL.
 *  - Bare hosts like `linkedin.com/company/x` get `https://` prefixed so
 *    URL() doesn't throw and the output is actually clickable.
 */
export default function UtmBulkTagger() {
    const [open, setOpen] = useState(false);
    const [urlsRaw, setUrlsRaw] = useState("");
    const [utm, setUtm] = useState({
        utm_source: "",
        utm_medium: "",
        utm_campaign: "",
        utm_content: "",
        utm_term: "",
    });
    const [copied, setCopied] = useState(false);

    const tagged = useMemo(() => _bulkTag(urlsRaw, utm), [urlsRaw, utm]);
    const validCount = tagged.filter((r) => r.ok).length;
    const errorCount = tagged.length - validCount;

    const copyAll = async () => {
        const text = tagged
            .filter((r) => r.ok)
            .map((r) => r.output)
            .join("\n");
        if (!text) {
            toast.error("Nothing to copy yet.");
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
            toast.success(`Copied ${validCount} tagged URL${validCount === 1 ? "" : "s"}`);
        } catch {
            toast.error("Copy failed — select the text manually.");
        }
    };

    const clear = () => {
        setUrlsRaw("");
        setUtm({ utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "" });
    };

    return (
        <section data-testid="utm-bulk-tagger" className="card-soft mt-8 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="utm-tagger-toggle"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition"
            >
                <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                    <Tag size={14} className="text-violet-500" />
                    UTM Bulk Tagger
                    <span className="text-[11px] text-muted-foreground font-normal ml-1">
                        · Paste URLs, tag them all at once
                    </span>
                </span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                    >
                        <div className="p-4 border-t border-border grid gap-4 lg:grid-cols-2">
                            {/* --- LEFT: inputs -------------------------------- */}
                            <div className="space-y-3">
                                <div>
                                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">URLs (one per line)</label>
                                    <textarea
                                        rows={8}
                                        value={urlsRaw}
                                        onChange={(e) => setUrlsRaw(e.target.value)}
                                        placeholder={"https://simonaratalent.us/employer/pricing\nhttps://simonaratalent.us/talent/pricing?ref=demo"}
                                        data-testid="utm-tagger-input-urls"
                                        spellCheck={false}
                                        className="mt-1 w-full font-mono text-xs px-3 py-2 rounded border border-border bg-background focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                                    />
                                    <div className="mt-1 text-[11px] text-muted-foreground">
                                        {urlsRaw.trim() ? (
                                            <>
                                                <span className="text-emerald-600 font-medium">{validCount} valid</span>
                                                {errorCount > 0 && <span className="text-rose-500 font-medium ml-2">{errorCount} invalid</span>}
                                            </>
                                        ) : "Paste your outbound links above."}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <UtmField
                                        label="utm_source *"
                                        placeholder="linkedin"
                                        value={utm.utm_source}
                                        onChange={(v) => setUtm({ ...utm, utm_source: v })}
                                        testId="utm-tagger-source"
                                    />
                                    <UtmField
                                        label="utm_medium"
                                        placeholder="social"
                                        value={utm.utm_medium}
                                        onChange={(v) => setUtm({ ...utm, utm_medium: v })}
                                        testId="utm-tagger-medium"
                                    />
                                    <UtmField
                                        label="utm_campaign"
                                        placeholder="launch_q1"
                                        value={utm.utm_campaign}
                                        onChange={(v) => setUtm({ ...utm, utm_campaign: v })}
                                        testId="utm-tagger-campaign"
                                    />
                                    <UtmField
                                        label="utm_content"
                                        placeholder="hero_cta"
                                        value={utm.utm_content}
                                        onChange={(v) => setUtm({ ...utm, utm_content: v })}
                                        testId="utm-tagger-content"
                                    />
                                    <UtmField
                                        label="utm_term"
                                        placeholder="hiring managers"
                                        value={utm.utm_term}
                                        onChange={(v) => setUtm({ ...utm, utm_term: v })}
                                        testId="utm-tagger-term"
                                    />
                                </div>

                                {!utm.utm_source && urlsRaw.trim() && (
                                    <div
                                        data-testid="utm-tagger-source-warning"
                                        className="text-[11px] px-3 py-2 rounded border border-amber-400/40 bg-amber-500/5 text-amber-700 dark:text-amber-300"
                                    >
                                        Heads up — no <code>utm_source</code> means these clicks will land in the
                                        &quot;Unattributed&quot; bucket. Set one before publishing.
                                    </div>
                                )}
                            </div>

                            {/* --- RIGHT: output ------------------------------- */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Tagged URLs</label>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={clear}
                                            data-testid="utm-tagger-clear"
                                            className="text-[11px] font-medium px-2 py-1 rounded border border-border hover:bg-muted/50 transition inline-flex items-center gap-1 text-muted-foreground"
                                        >
                                            <Trash2 size={11} /> Clear
                                        </button>
                                        <button
                                            type="button"
                                            onClick={copyAll}
                                            disabled={validCount === 0}
                                            data-testid="utm-tagger-copy"
                                            className="text-[11px] font-medium px-2 py-1 rounded bg-violet-600 text-white hover:bg-violet-700 transition inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy all</>}
                                        </button>
                                    </div>
                                </div>
                                <div
                                    data-testid="utm-tagger-output"
                                    className="h-[220px] overflow-auto rounded border border-border bg-muted/20 p-2 font-mono text-[11px] leading-relaxed"
                                >
                                    {tagged.length === 0 && (
                                        <div className="text-muted-foreground italic px-1 py-1">Output will appear here as you type…</div>
                                    )}
                                    {tagged.map((row, i) => (
                                        <div
                                            key={i}
                                            data-testid={`utm-tagger-row-${i}`}
                                            className={`px-2 py-1 rounded ${row.ok ? "hover:bg-background" : "text-rose-500"}`}
                                            title={row.ok ? "Click to copy" : row.error}
                                            onClick={() => {
                                                if (!row.ok) return;
                                                navigator.clipboard.writeText(row.output).then(() => {
                                                    toast.success("Copied line to clipboard");
                                                }).catch(() => { /* silent */ });
                                            }}
                                        >
                                            {row.ok ? row.output : `⚠︎  ${row.input}  —  ${row.error}`}
                                        </div>
                                    ))}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    Existing UTM params on your URLs get overwritten. Other query params
                                    (like <code>?ref=</code>) are preserved. Click a single row to copy just
                                    that one.
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

const UtmField = ({ label, value, onChange, placeholder, testId }) => (
    <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</label>
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testId}
            spellCheck={false}
            className="mt-1 w-full px-2 py-1.5 rounded border border-border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500/30"
        />
    </div>
);

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests / future reuse)
// ---------------------------------------------------------------------------
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/**
 * Split a raw textarea into lines, ignore blanks + full-line comments (# …),
 * and tag each with the given UTM params.
 *
 * Returns an array of `{ input, output, ok, error }` in input order so the
 * UI can render each line with its own success/error state.
 */
export function _bulkTag(rawText, utm) {
    const lines = (rawText || "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l && !l.startsWith("#"));

    return lines.map((line) => _tagOne(line, utm));
}

export function _tagOne(rawUrl, utm) {
    const inputForDisplay = rawUrl;
    // URL() requires a protocol — assume https for bare hosts. This matches
    // what most ops folks would paste from a spreadsheet.
    const withProto = /^[a-z][a-z0-9+.-]*:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
    let u;
    try {
        u = new URL(withProto);
    } catch {
        return { input: inputForDisplay, output: rawUrl, ok: false, error: "not a valid URL" };
    }
    // Overwrite existing UTM params, preserve everything else.
    for (const k of UTM_KEYS) {
        u.searchParams.delete(k);
    }
    for (const k of UTM_KEYS) {
        const v = (utm?.[k] || "").trim();
        if (v) u.searchParams.set(k, v);
    }
    return { input: inputForDisplay, output: u.toString(), ok: true };
}
